// ft3d-hud/active-window.ts — one active-window contract across the FT3D window families.
//
// Campaign windows, the shared Dice/Forms windows, and the floating inventory windows are built by
// different features, but they should feel like one desktop. A pointer press inside a window makes
// it active; a press anywhere else (including the same-origin scene iframe) clears that state.
// Escape closes only the active window by clicking its canonical close control, so every window's
// existing persistence, toggle, and cleanup path remains authoritative.

import {
    applyTransientFt3dWindowPosition,
    clearTransientFt3dWindowPosition,
} from './draggable';

const WINDOW_SELECTOR = [
    '[data-ft3d-window]',
    '[data-ft3d-dice-window]',
    '[data-ft3d-forms-window]',
    '.ft-bagwin',
].join(', ');

const CLOSE_SELECTOR = [
    '[data-ft3d-window-close]',
    '[data-ft3d-dice-close]',
    '[data-ft3d-forms-close]',
    '.ft-bagwin__close',
].join(', ');

const ACTIVE_CLASS = 'is-ft3d-window-active';
const ACTIVE_CONTROL_CLASS = 'is-ft3d-window-control-active';
const HAS_ACTIVE_CLASS = 'ft3d-window-has-active';
const CAMPAIGN_SETTINGS_SELECTOR = '[data-ft3d-window="settings"]';
const MOBILE_CAMPAIGN_SHEET_SELECTOR = [
    '[data-ft3d-window="settings"]',
    '[data-ft3d-window="workspace"]',
    // Includes the canonical actor sheet and dynamically-created inspected-caster sheets. On a
    // phone they all become the same edge-to-edge surface, so only the activated one may remain.
    '.ft3d-campaign-window--actor',
].join(', ');
const CAMPAIGN_WINDOW_BAR_SELECTOR = ':scope > .ft3d-campaign-window__bar';
const MIN_REACHABLE_HEADER_WIDTH = 96;
const mobileCampaignSheetLayout = window.matchMedia('(max-width: 767.98px)');

let activeWindow: HTMLElement | null = null;
let mounted = false;
let resizeFrame = 0;
const boundFrames = new WeakSet<HTMLIFrameElement>();

function managedWindow(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    return target.closest<HTMLElement>(WINDOW_SELECTOR);
}

function isAvailable(win: HTMLElement | null): win is HTMLElement {
    if (!win || !win.isConnected || win.hidden) return false;
    return window.getComputedStyle(win).display !== 'none';
}

function clearAutoRevealedPosition(win: HTMLElement): void {
    if (!win.hasAttribute('data-ft3d-auto-revealed-by')) return;
    clearTransientFt3dWindowPosition(win);
    win.removeAttribute('data-ft3d-auto-revealed-by');
}

/**
 * Settings is a centred dialog on desktop, where it can coexist with Workspace and Actor. On the
 * phone layout all three become the same edge-to-edge sheet, so keeping more than one open makes
 * the older sheet's entire title bar unreachable. Close covered mobile siblings through their
 * canonical controls so each feature's persisted open state and toolbar affordance stay in sync.
 */
function closeCoveredMobileCampaignSheets(front: HTMLElement): boolean {
    if (!mobileCampaignSheetLayout.matches || !front.matches(MOBILE_CAMPAIGN_SHEET_SELECTOR)) return false;
    let closedAny = false;
    document.querySelectorAll<HTMLElement>(MOBILE_CAMPAIGN_SHEET_SELECTOR).forEach((candidate) => {
        if (candidate === front || !isAvailable(candidate)) return;
        const close = candidate.querySelector<HTMLElement>('[data-ft3d-window-close]');
        if (!close) return;
        clearAutoRevealedPosition(candidate);
        close.click();
        closedAny = true;
    });
    return closedAny;
}

/**
 * A remembered Settings position can sit wholly inside Workspace. When Workspace is activated it
 * correctly rises to the front, but that used to bury every pixel of the Settings title bar and
 * leave the still-open dialog unreachable. Preserve the active-window ordering and move only the
 * covered dialog, just far enough to expose a useful piece of its header. This is intentionally an
 * in-session correction: the viewer's remembered position remains untouched until they drag again.
 */
function keepCoveredCampaignSettingsReachable(front: HTMLElement): void {
    if (!front.matches('.ft3d-campaign-window')) return;
    const settings = document.querySelector<HTMLElement>(CAMPAIGN_SETTINGS_SELECTOR);
    if (!isAvailable(settings)) return;
    if (front === settings) {
        clearAutoRevealedPosition(settings);
        return;
    }

    const frontZ = Number.parseFloat(window.getComputedStyle(front).zIndex);
    const settingsZ = Number.parseFloat(window.getComputedStyle(settings).zIndex);
    if (!Number.isFinite(frontZ) || !Number.isFinite(settingsZ) || frontZ <= settingsZ) {
        clearAutoRevealedPosition(settings);
        return;
    }

    const bar = settings.querySelector<HTMLElement>(CAMPAIGN_WINDOW_BAR_SELECTOR);
    if (!bar) return;
    const frontRect = front.getBoundingClientRect();
    const barRect = bar.getBoundingClientRect();
    const fullyCovered = frontRect.left <= barRect.left
        && frontRect.right >= barRect.right
        && frontRect.top <= barRect.top
        && frontRect.bottom >= barRect.bottom;
    if (!fullyCovered) {
        clearAutoRevealedPosition(settings);
        return;
    }

    const settingsRect = settings.getBoundingClientRect();
    const frame = settings.closest<HTMLElement>('[data-ft3d-campaign-encounter-view]');
    const frameRect = frame?.getBoundingClientRect()
        ?? new DOMRect(0, 0, window.innerWidth, window.innerHeight);
    const candidates = [
        // Expose the right end first when it is the shortest move: that is where the close control is.
        frontRect.right - settingsRect.width + MIN_REACHABLE_HEADER_WIDTH,
        // Otherwise expose the left end, where the window title and drag surface begin.
        frontRect.left - MIN_REACHABLE_HEADER_WIDTH,
    ].filter(left => left >= frameRect.left
        && left + settingsRect.width <= frameRect.right);
    if (candidates.length === 0) return;

    const left = candidates.reduce((nearest, candidate) => (
        Math.abs(candidate - settingsRect.left) < Math.abs(nearest - settingsRect.left)
            ? candidate
            : nearest
    ));
    applyTransientFt3dWindowPosition(
        settings,
        frame,
        left - frameRect.left,
        settingsRect.top - frameRect.top,
    );
    settings.setAttribute('data-ft3d-auto-revealed-by', front.id || 'campaign-window');
}

function reconcileCampaignWindowGeometry(front: HTMLElement): void {
    if (closeCoveredMobileCampaignSheets(front)) return;
    keepCoveredCampaignSettingsReachable(front);
}

function controlDocuments(): Document[] {
    const documents = [document];
    document.querySelectorAll<HTMLIFrameElement>('iframe').forEach((frame) => {
        try {
            if (frame.contentDocument) documents.push(frame.contentDocument);
        } catch { /* transient/cross-origin frame */ }
    });
    return documents;
}

function syncActiveControls(): void {
    const activeId = activeWindow?.id ?? '';
    controlDocuments().forEach((controlDocument) => {
        controlDocument.querySelectorAll<HTMLElement>(`.${ACTIVE_CONTROL_CLASS}`).forEach((control) => {
            control.classList.remove(ACTIVE_CONTROL_CLASS);
            control.removeAttribute('data-ft3d-window-control-active');
        });
        if (!activeId) return;
        controlDocument.querySelectorAll<HTMLElement>('[aria-controls]').forEach((control) => {
            if (control.getAttribute('aria-controls') !== activeId) return;
            control.classList.add(ACTIVE_CONTROL_CLASS);
            control.setAttribute('data-ft3d-window-control-active', 'true');
        });
        // Settings is parent-owned while its canonical gear lives inside the campaign iframe and
        // therefore cannot point at the parent's element with a meaningful local aria-controls.
        if (activeId === 'ft3d_campaign_settings_window') {
            controlDocument.querySelectorAll<HTMLElement>('[data-ft3d-minimap-settings]').forEach((control) => {
                control.classList.add(ACTIVE_CONTROL_CLASS);
                control.setAttribute('data-ft3d-window-control-active', 'true');
            });
        }
    });
}

export function setActiveFt3dWindow(win: HTMLElement | null): void {
    const next = isAvailable(win) ? win : null;
    if (activeWindow === next) return;

    if (activeWindow) {
        activeWindow.classList.remove(ACTIVE_CLASS);
        activeWindow.removeAttribute('data-ft3d-window-active');
    }
    activeWindow = next;
    document.documentElement.classList.toggle(HAS_ACTIVE_CLASS, activeWindow !== null);
    if (!activeWindow) {
        syncActiveControls();
        return;
    }

    activeWindow.classList.add(ACTIVE_CLASS);
    activeWindow.setAttribute('data-ft3d-window-active', 'true');
    reconcileCampaignWindowGeometry(activeWindow);
    syncActiveControls();
}

export function hasActiveFt3dWindow(): boolean {
    if (!isAvailable(activeWindow)) setActiveFt3dWindow(null);
    return activeWindow !== null;
}

export function closeActiveFt3dWindow(): boolean {
    if (!hasActiveFt3dWindow() || !activeWindow) return false;
    const close = activeWindow.querySelector<HTMLElement>(CLOSE_SELECTOR);
    if (!close) return false;
    close.click();
    return true;
}

function hasHigherPriorityEscapeSurface(): boolean {
    return Boolean(document.querySelector('.modal.show, .offcanvas.show, .dropdown-menu.show'));
}

function handleEscape(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || event.repeat || event.defaultPrevented || hasHigherPriorityEscapeSurface()) return;
    if (!closeActiveFt3dWindow()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
}

function bindFrame(frame: HTMLIFrameElement): void {
    if (boundFrames.has(frame)) return;
    boundFrames.add(frame);

    const bindDocument = (): void => {
        try {
            const frameDocument = frame.contentDocument;
            const frameWindow = frame.contentWindow;
            if (!frameDocument || !frameWindow) return;
            // A scene click is outside every parent window even though iframe events do not bubble
            // into the parent document. The parent campaign shortcut is capture-phase and therefore
            // still gets first refusal for targeting/placement cancellation before this bubble handler.
            frameDocument.addEventListener('pointerdown', () => setActiveFt3dWindow(null), true);
            frameDocument.addEventListener('focusin', () => setActiveFt3dWindow(null), true);
            frameWindow.addEventListener('keydown', handleEscape);
            syncActiveControls();
        } catch {
            // FT3D is same-origin in production. Ignore a transient/cross-origin navigation boundary.
        }
    };

    frame.addEventListener('load', bindDocument);
    bindDocument();
}

function activateNewWindows(node: Node): void {
    if (!(node instanceof Element)) return;
    const candidates: HTMLElement[] = [];
    if (node.matches(WINDOW_SELECTOR)) candidates.push(node as HTMLElement);
    node.querySelectorAll<HTMLElement>(WINDOW_SELECTOR).forEach((win) => candidates.push(win));
    // A feature may create a family in one synchronous render. The last visible window is the one
    // it most recently placed on top (the inventory manager uses this for its restored sections).
    candidates.forEach((win) => {
        if (isAvailable(win)) setActiveFt3dWindow(win);
    });
    if (node instanceof HTMLIFrameElement) bindFrame(node);
    node.querySelectorAll<HTMLIFrameElement>('iframe').forEach(bindFrame);
}

export function mountFt3dActiveWindows(): void {
    if (mounted) return;
    mounted = true;

    document.addEventListener('pointerdown', (event) => {
        const win = managedWindow(event.target);
        setActiveFt3dWindow(isAvailable(win) ? win : null);
    }, true);
    document.addEventListener('focusin', (event) => {
        const win = managedWindow(event.target);
        setActiveFt3dWindow(isAvailable(win) ? win : null);
    }, true);
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', () => {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
            resizeFrame = 0;
            if (isAvailable(activeWindow)) reconcileCampaignWindowGeometry(activeWindow);
        });
    });

    document.querySelectorAll<HTMLIFrameElement>('iframe').forEach(bindFrame);

    const observer = new MutationObserver((records) => {
        for (const record of records) {
            if (record.type === 'attributes') {
                const win = record.target instanceof HTMLElement && record.target.matches(WINDOW_SELECTOR)
                    ? record.target
                    : null;
                if (!win) continue;
                if (isAvailable(win) && record.attributeName === 'hidden') setActiveFt3dWindow(win);
                else if (win === activeWindow && !isAvailable(win)) setActiveFt3dWindow(null);
                continue;
            }
            record.addedNodes.forEach(activateNewWindows);
        }
        if (activeWindow && !activeWindow.isConnected) setActiveFt3dWindow(null);
        else if (activeWindow) syncActiveControls();
    });
    observer.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['hidden'],
    });
}

Object.assign(window, {
    ftCloseActiveFt3dWindow: closeActiveFt3dWindow,
    ftHasActiveFt3dWindow: hasActiveFt3dWindow,
    ftSetActiveFt3dWindow: setActiveFt3dWindow,
});
